import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { CLUB_LOGO } from "@/lib/api";
import { Menu, X, Home, Calendar, Newspaper, Users, HelpCircle, Phone, FileText, Mail, Lock, UserPlus, ArrowLeft, Dumbbell, Trophy, Camera, Share2, Flag, Zap } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import AlertBanner from "@/components/AlertBanner";

const NAV = [
  { to: "/", label: "Accueil", icon: Home, testId: "nav-home" },
  { to: "/inscriptions", label: "Inscriptions", icon: UserPlus, testId: "nav-inscriptions" },
  { to: "/recrutement", label: "Devenir arbitre", icon: Flag, testId: "nav-recrutement" },
  { to: "/entrainements", label: "Entraînements", icon: Dumbbell, testId: "nav-trainings" },
  { to: "/stages", label: "Stages", icon: Trophy, testId: "nav-stages" },
  { to: "/calendrier", label: "Calendrier", icon: Calendar, testId: "nav-calendar" },
  { to: "/live-matchs", label: "Live Matchs", icon: Zap, testId: "nav-live", highlight: true },
  { to: "/classements", label: "Classements", icon: Trophy, testId: "nav-classements" },
  { to: "/galerie", label: "Galerie", icon: Camera, testId: "nav-gallery" },
  { to: "/reseaux-sociaux", label: "Réseaux", icon: Share2, testId: "nav-social" },
  { to: "/actualites", label: "Actualités", icon: Newspaper, testId: "nav-news" },
  { to: "/faq", label: "FAQ", icon: HelpCircle, testId: "nav-faq" },
  { to: "/contacts", label: "Contacts", icon: Phone, testId: "nav-contacts" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();
  const isAdmin = loc.pathname.startsWith("/admin");
  const isHome = loc.pathname === "/";

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <AlertBanner />
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 glass-nav" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4 h-16 lg:h-20">
            <Link to="/" className="flex items-center gap-3 group shrink-0 mr-auto" data-testid="logo-link">
              <img src={CLUB_LOGO} alt="Villeurbanne United" className="h-10 w-10 lg:h-12 lg:w-12 object-contain" />
              <div className="hidden sm:block">
                <div className="font-bold text-sm lg:text-base text-[#0F172A] leading-tight whitespace-nowrap" style={{ fontFamily: "Outfit" }}>
                  Villeurbanne United
                </div>
                <div className="text-[10px] lg:text-xs tracking-[0.2em] uppercase text-[#1E3A8A] font-semibold whitespace-nowrap">
                  Football Club · 2017
                </div>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1" data-testid="desktop-nav">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `px-2 xl:px-3 py-2 text-xs xl:text-sm font-medium rounded-md transition-colors duration-200 whitespace-nowrap ${
                      isActive
                        ? "text-[#1E3A8A] bg-blue-50"
                        : "text-[#475569] hover:text-[#1E3A8A] hover:bg-slate-50"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="nav-cta-contact"
                className="hidden xl:inline-block ml-2 px-4 py-2 text-sm font-semibold rounded-md bg-[#1E3A8A] text-white hover:bg-[#1E40AF] transition-colors duration-200"
              >
                Nous écrire
              </Link>
            </nav>

            {/* Mobile burger */}
            <button
              className="lg:hidden p-2 text-[#0F172A] rounded-md hover:bg-slate-100 transition-colors"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-[#E2E8F0] bg-white" data-testid="mobile-menu">
            <nav className="px-4 py-3 space-y-1 max-w-7xl mx-auto">
              {NAV.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`${item.testId}-mobile`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-[#1E3A8A] text-white"
                          : "text-[#0F172A] hover:bg-slate-100"
                      }`
                    }
                  >
                    <Icon size={18} />
                    {item.label}
                  </NavLink>
                );
              })}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="nav-cta-contact-mobile"
                className="flex items-center gap-3 px-3 py-3 rounded-md bg-[#D97706] text-white font-semibold"
              >
                <Mail size={18} />
                Formulaire de contact
              </Link>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1" data-testid="main-content">
        {!isHome && !isAdmin && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6">
            <button
              onClick={goBack}
              data-testid="back-button"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#475569] hover:text-[#1E3A8A] transition-colors group"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
              Retour
            </button>
          </div>
        )}
        <Outlet />
      </main>

      {/* Footer */}
      {!isAdmin && (
        <footer className="mt-16 border-t border-[#E2E8F0] bg-[#0F172A] text-slate-200" data-testid="site-footer">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3">
                <img src={CLUB_LOGO} alt="VU" className="h-14 w-14 object-contain bg-white rounded-full p-1" />
                <div>
                  <div className="font-bold text-lg text-white" style={{ fontFamily: "Outfit" }}>Villeurbanne United FC</div>
                  <div className="text-xs tracking-[0.2em] uppercase text-[#D97706]">Fierté · Rigueur · Collectif</div>
                </div>
              </div>
              <p className="mt-5 text-sm text-slate-400 max-w-md leading-relaxed">
                Club de football amateur villeurbannais fondé en 2017, formant les jeunes talents du quartier.
                Ce site est votre outil officiel : <strong className="text-slate-200">toutes vos questions trouvent leur réponse en un clic</strong>, où que vous soyez et à toute heure.
              </p>
              <div className="mt-5 flex items-center gap-3" data-testid="footer-socials">
                <a href="https://www.tiktok.com/@villeurbanne_united1" target="_blank" rel="noopener noreferrer" aria-label="TikTok Villeurbanne United" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-black flex items-center justify-center transition-colors" data-testid="footer-tiktok">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true" className="text-white">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1Z"/>
                  </svg>
                </a>
                <a href="https://instagram.com/villeurbanne_united_football" target="_blank" rel="noopener noreferrer" aria-label="Instagram Villeurbanne United" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#FD1D1D] hover:to-[#FCB045] flex items-center justify-center transition-all" data-testid="footer-instagram">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                </a>
                <Link to="/reseaux-sociaux" className="ml-1 text-xs text-slate-400 hover:text-[#D97706] transition-colors underline underline-offset-2">
                  Tous nos réseaux
                </Link>
              </div>
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-slate-400 font-bold">Navigation</div>
              <ul className="mt-4 space-y-2 text-sm">
                {NAV.slice(0, 5).map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="hover:text-[#D97706] transition-colors">{n.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-slate-400 font-bold">Contact</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-400">
                <li>171 rue Anatole France</li>
                <li>69100 Villeurbanne</li>
                <li>Secrétariat : 18h – 20h</li>
                <li>
                  <Link to="/contact" className="text-[#D97706] hover:underline">Formulaire de contact →</Link>
                </li>
                <li className="pt-3">
                  <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300" data-testid="admin-footer-link">
                    <Lock size={12} /> Espace staff
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-500">
            © {new Date().getFullYear()} Villeurbanne United FC · Tous droits réservés
          </div>
        </footer>
      )}
      <Toaster position="top-right" />
    </div>
  );
}
